import { FORBIDDEN } from '@mm/constants/status-codes';
import { Dispatch } from 'redux';
import RssFeedController from '../controllers/RssFeedController';
import commonHandler, {
  permissionsHandler,
} from '../errorHandlers/common-handler';
import RssFeedModel from '../model/RssFeedModel';
import { StoreState } from '../store-interfaces';
import { getActionHookBuilder } from './action-hook-builder';
import ConnectedPageCRUDActions from './ConnectedPageCRUDActions';
import CrudActionContainer from './CrudActionContainer';
import { showFacebookConnectModal } from './networkActions';
import { selectRssFeed, unselectRssFeed } from './viewActions';

type ReduxDispatch = Dispatch<StoreState>;

class RssFeedCRUDActions extends CrudActionContainer {
  resetAction: string;

  constructor() {
    super('RSS_FEEDS');
    this.resetAction = super.makeActionKey('RESET');
  }

  create(botId: number, entity: Partial<RssFeedModel>) {
    return async (dispatch: ReduxDispatch) => {
      try {
        dispatch(this.busy(this.createAction));
        const response = await RssFeedController.postRssFeed(botId, entity);
        dispatch(this.retrieved(response));
        dispatch(selectRssFeed(response));
        return response;
      } catch (e) {
        return permissionsHandler(dispatch, this)(e);
      }
    };
  }

  retrieveAll(botId: number) {
    return async (dispatch: ReduxDispatch) => {
      try {
        dispatch(this.busy(this.retrieveAllAction));
        const response = await RssFeedController.getAll(botId);
        dispatch(this.retrievedAll(response));
        return response;
      } catch (e) {
        if (e.status === FORBIDDEN) {
          dispatch(
            showFacebookConnectModal(
              'Please give us permissions to your account',
              false,
              'ALL_PERMISSIONS',
            ),
          );
          dispatch(ConnectedPageCRUDActions.unbusy());
          dispatch(this.unbusy());
        } else {
          return commonHandler(dispatch)(e);
        }
      }
    };
  }

  retrieveById(botId: number, rssFeedId: number) {
    return async (dispatch: ReduxDispatch) => {
      try {
        const response = await RssFeedController.getById(botId, rssFeedId);
        dispatch(selectRssFeed(response));
        return response;
      } catch (e) {
        return commonHandler(dispatch)(e);
      }
    };
  }

  update(botId: number, entity: Partial<RssFeedModel>) {
    return async (dispatch: ReduxDispatch) => {
      try {
        dispatch(this.busy(this.updateAction));
        const response = await RssFeedController.putRssFeed(botId, entity);
        dispatch(this.updateLocal(response));
        dispatch(selectRssFeed(response));
        return response;
      } catch (e) {
        return permissionsHandler(dispatch, this)(e);
      }
    };
  }

  delete(botId: number, entity: RssFeedModel) {
    return async (dispatch: ReduxDispatch) => {
      const { id } = entity;
      try {
        dispatch(this.busy(this.deleteLocalAction));
        dispatch(this.deleteLocal(entity));
        const response = await RssFeedController.deleteRssFeed(botId, id);
        dispatch(this.retrieveAll(botId));
        dispatch(unselectRssFeed());
        return response;
      } catch (e) {
        return commonHandler(dispatch)(e);
      }
    };
  }

  reset() {
    return {
      type: this.resetAction,
    };
  }
}

const rssFeedCRUDActions = new RssFeedCRUDActions();

export const useRssFeedCRUDActions = getActionHookBuilder(rssFeedCRUDActions, [
  'update',
  'retrieveAll',
  'create',
  'delete',
]);

export default rssFeedCRUDActions;
